import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { fetchOrganizations } from 'src/store/slices/organization.slice';
import {
  buildFlowNameLookup,
  extractOrgFlows,
} from 'src/services/organizations.service';

const API_HOSTS = {
  staging: 'https://staging.id.sonio-group.com/',
  prod: 'https://id.sonio-group.com/',
} as const;

const ID_API_PROXIES = {
  staging: '/id-proxy/staging/',
  prod: '/id-proxy/prod/',
} as const;

const ApiDocsPage = () => {
  const navigate = useNavigate();
  const { user, getIdTokenClaims } = useAuth0();
  const dispatch = useAppDispatch();
  const { selectedOrganization } = useAppSelector(state => state.organization);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [selectedFlowId, setSelectedFlowId] = useState('');

  const flows = useMemo(
    () => (selectedOrganization ? extractOrgFlows(selectedOrganization) : []),
    [selectedOrganization],
  );

  const flowNameLookup = useMemo(
    () => buildFlowNameLookup(selectedOrganization ? [selectedOrganization] : []),
    [selectedOrganization],
  );

  useEffect(() => {
    setSelectedFlowId('');
  }, [selectedOrganization?.id]);

  useEffect(() => {
    if (!user?.sub) return;
    getIdTokenClaims().then(claims => {
      if (claims?.__raw) {
        dispatch(fetchOrganizations({ sub: user.sub!, __raw: claims.__raw }));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sub]);

  useEffect(() => {
    const onMessage = async (event: MessageEvent) => {
      if (event.data?.type === 'SONIO_NAVIGATE_HOME') {
        navigate('/home');
        return;
      }
      if (event.data?.type === 'SONIO_NAVIGATE' && event.data.path) {
        navigate(event.data.path);
        return;
      }

      if (event.data?.type === 'SONIO_FLOW_SELECTED') {
        setSelectedFlowId(event.data.flowId || '');
        return;
      }

      if (event.data?.type !== 'SONIO_TRY_IT_REQUEST') return;

      const { requestId, env, method, path, body, apiKey } = event.data;
      const proxyBase =
        ID_API_PROXIES[env as keyof typeof ID_API_PROXIES] ||
        ID_API_PROXIES.staging;
      const publicBase =
        API_HOSTS[env as keyof typeof API_HOSTS] || API_HOSTS.staging;
      const relativePath = String(path).replace(/^\//, '');
      const url = `${proxyBase}${relativePath}`;
      const publicUrl = `${publicBase}${relativePath}`;

      const respond = (payload: Record<string, unknown>) => {
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'SONIO_TRY_IT_RESPONSE', requestId, ...payload },
          '*',
        );
      };

      try {
        const httpMethod = String(method || 'GET').toUpperCase();
        const headers: Record<string, string> = {};
        if (httpMethod !== 'GET' && httpMethod !== 'HEAD') {
          headers['Content-Type'] = 'application/json';
        }
        if (apiKey) {
          headers['x-sonio-api-key'] = String(apiKey);
        }

        const fetchOptions: RequestInit = {
          method: httpMethod,
          headers,
        };

        if (httpMethod !== 'GET' && httpMethod !== 'HEAD' && body != null) {
          fetchOptions.body = JSON.stringify(body);
        }

        const response = await fetch(url, fetchOptions);
        const text = await response.text();
        let parsedBody: unknown = text;
        try {
          parsedBody = JSON.parse(text);
        } catch {
          // keep raw text
        }

        respond({
          status: response.status,
          body: parsedBody,
          url: publicUrl,
        });
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : 'Request failed';
        respond({ error: message, url: publicUrl });
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [getIdTokenClaims, navigate]);

  const sendToIframe = () => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;

    win.postMessage(
      {
        type: 'SONIO_FLOWS',
        flows: flows.map(flow => ({
          flowId: flow.flowId,
          flowName: flow.flowName || flowNameLookup[flow.flowId] || flow.flowId,
        })),
        selectedFlowId,
      },
      '*',
    );

    win.postMessage(
      {
        type: 'SONIO_CONTEXT',
        customerId: selectedOrganization?.customerId ?? '',
        flowId: selectedFlowId,
        flowName: selectedFlowId
          ? (flowNameLookup[selectedFlowId] || selectedFlowId)
          : '',
      },
      '*',
    );
  };

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const onLoad = () => sendToIframe();
    iframe.addEventListener('load', onLoad);
    sendToIframe();
    return () => iframe.removeEventListener('load', onLoad);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization, selectedFlowId, flows]);

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-white">
      <iframe
        ref={iframeRef}
        src="/sonio-docs-export.html"
        className="block h-full w-full border-0"
        title="SONIO API Documentation"
      />
    </div>
  );
};

export default ApiDocsPage;
